import { saveAs } from "file-saver";
import ExcelJS, { RichText } from "exceljs";
import { getDescription } from "reedy-data";
import {
  getKeywordsByType,
  IProduct,
  keywordsToText,
  qualifierTypes,
  SubjectSchemeIdentifier,
} from "integrations/firebase/collections";
import { formatFirestoreTimestamp } from "shared/utils";
import { filterDescriptions } from "shared/constants";
import {
  FirestoreProductBisacSubject,
  FirestoreProductSubject,
} from "reedy-data/models";
import {
  getDatabaseBisacSubjects,
  getFinalBisacSubjects,
} from "shared/bisac/productBisacSubjects";

type ExcelCellValue = string | { richText: RichText[] };

enum ExportProductColumns {
  Isbn = "ISBN",
  Title = "Title",
  Author = "Author",
  Publisher = "Publisher",
  PublishedDate = "Published date",
  Genre = "Genre",
  Form = "Form",
  DatabaseKeywords = "Database Keywords",
  GeneratedKeywords = "Generated Keywords",
  DatabaseThemaCodes = "Database Thema-Codes",
  GeneratedThemaCodes = "Generated Thema-Codes",
  DatabaseBisacCodes = "Database BISAC Codes",
  FinalBisacCodes = "Final BISAC Codes",
  Filename = "Filename",
  GeneratedDescription = "Generated Description",
  FinalList = "Final List",
}

const includedThemaSubjectTypes = [
  ...qualifierTypes,
  SubjectSchemeIdentifier.ThemaSubjectCategory,
] as string[];

const formatProductProperty = (property?: string[] | any): string => {
  const EMPTY_SPACE = " ";

  if (property === null || property === undefined) {
    return EMPTY_SPACE;
  }

  return (
    (Array.isArray(property) ? property.join(";") : String(property)) ||
    EMPTY_SPACE
  );
};

const formatThemaCodes = (
  subjects: FirestoreProductSubject[],
): ExcelCellValue => {
  const themaCodes = subjects.filter((subject) =>
    includedThemaSubjectTypes.includes(subject.subjectSchemeIdentifier ?? ""),
  );

  if (themaCodes.length === 0) {
    return formatProductProperty("");
  }

  return {
    richText: themaCodes
      .map((code) => [
        {
          text: `${code.subjectCode} ${code.subjectHeadingText}: `,
          font: { bold: true },
        },
        { text: code.reasoning || "", font: { bold: false } },
        { text: "\n", font: { bold: false } },
      ])
      .flat(),
  };
};

const formatBisacCodes = (
  subjects: FirestoreProductBisacSubject[],
): ExcelCellValue =>
  formatProductProperty(
    subjects
      .map(({ subjectCode, subjectHeadingText }) =>
        [subjectCode, subjectHeadingText].filter(Boolean).join(" "),
      )
      .join("\n"),
  );

const formatProducts = (products: IProduct[]) =>
  products.map((p) => {
    const keywords = getKeywordsByType(p);
    const formattedData: Record<string, ExcelCellValue> = {
      [ExportProductColumns.Isbn]: formatProductProperty(p.isbn),
      [ExportProductColumns.Title]: formatProductProperty(p.title),
      [ExportProductColumns.Author]: formatProductProperty(p.author),
      [ExportProductColumns.Publisher]: formatProductProperty(p.publisher),
      [ExportProductColumns.PublishedDate]: formatProductProperty(
        formatFirestoreTimestamp(p.publishedAt),
      ),
      [ExportProductColumns.Genre]: formatProductProperty(p.genre),
      [ExportProductColumns.Form]: formatProductProperty(
        p.productGroupDescription,
      ),
      [ExportProductColumns.DatabaseKeywords]: formatProductProperty(
        keywordsToText(keywords.database),
      ),
      [ExportProductColumns.GeneratedKeywords]: formatProductProperty(
        keywordsToText(keywords.generated),
      ),
      [ExportProductColumns.FinalList]: formatProductProperty(
        keywordsToText(keywords.final),
      ),
      [ExportProductColumns.DatabaseThemaCodes]: formatThemaCodes(
        p.subjects?.database || [],
      ),
      [ExportProductColumns.GeneratedThemaCodes]: formatThemaCodes(
        p.subjects?.generated || [],
      ),
      [ExportProductColumns.DatabaseBisacCodes]: formatBisacCodes(
        getDatabaseBisacSubjects(p.subjects?.bisac ?? []),
      ),
      [ExportProductColumns.FinalBisacCodes]: formatBisacCodes(
        getFinalBisacSubjects(p.subjects?.bisac ?? []),
      ),
    };

    // Filter descriptions and add them with their hash as column prefix
    const filteredDescriptions = filterDescriptions(p.descriptions || []);
    filteredDescriptions?.forEach((desc) => {
      const prefix = `${desc.textType ?? "DEFAULT"} [${desc.audiences?.join(",")}]`;
      formattedData[`${prefix}: Database Description`] = formatProductProperty(
        desc.text,
      );
      formattedData[`${prefix}: Generated Description`] = formatProductProperty(
        desc.textGenerated,
      );
    });

    return formattedData;
  });

const saveAsExcel = async (
  headers: string[],
  rows: ExcelCellValue[][],
  filename: string,
) => {
  const fileType =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("data");
  const headerRow = worksheet.addRow(headers);

  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
  });

  rows.forEach((row) => worksheet.addRow(row));
  // Set column widths
  worksheet.getColumn(1).width = 30; // File Name
  worksheet.getColumn(2).width = 50; // Description
  worksheet.getColumn(3).width = 40; // Keywords
  worksheet.getColumn(4).width = 60; // Thema-Codes
  // Enable text wrapping for all cells
  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      cell.alignment = {
        wrapText: true,
        vertical: "top",
      };
    });
  });
  const excelBuffer = await workbook.xlsx.writeBuffer();
  const dataBlob = new Blob([excelBuffer], { type: fileType });

  return saveAs(dataBlob, filename + fileExtension);
};

export const saveProductsAsExcel = async (
  products: IProduct[],
  filename: string,
) => {
  const baseHeaders = [
    ExportProductColumns.Isbn,
    ExportProductColumns.Title,
    ExportProductColumns.Author,
    ExportProductColumns.Publisher,
    ExportProductColumns.PublishedDate,
    ExportProductColumns.Genre,
    ExportProductColumns.Form,
    ExportProductColumns.DatabaseKeywords,
    ExportProductColumns.GeneratedKeywords,
    ExportProductColumns.FinalList,
    ExportProductColumns.DatabaseThemaCodes,
    ExportProductColumns.GeneratedThemaCodes,
    ExportProductColumns.DatabaseBisacCodes,
    ExportProductColumns.FinalBisacCodes,
  ];

  // Get all unique description column headers from the formatted data
  const formattedData = formatProducts(products);
  const descriptionHeaders = Array.from(
    new Set(
      formattedData.flatMap((data) =>
        Object.keys(data).filter(
          (key) =>
            key.includes("Database Description") ||
            key.includes("Generated Description"),
        ),
      ),
    ),
  ).sort();

  const headers = [...baseHeaders, ...descriptionHeaders];
  const rows = formattedData.map((data) =>
    headers.map((header) => data[header] || ""),
  );

  return saveAsExcel(headers, rows, filename);
};

export const saveProductAsExcel = async (
  product: IProduct,
  filename: string,
) => {
  return saveProductsAsExcel([product], filename);
};

export const saveManuscriptProductAsExcel = async (
  product: IProduct,
  filename: string,
) => {
  const headers = [
    ExportProductColumns.Filename,
    ExportProductColumns.GeneratedKeywords,
    ExportProductColumns.GeneratedThemaCodes,
    ExportProductColumns.GeneratedDescription,
  ];

  const keywords = getKeywordsByType(product);
  const themaCodes = (product?.subjects?.generated || []).filter((subject) =>
    includedThemaSubjectTypes.includes(subject.subjectSchemeIdentifier ?? ""),
  );
  const defaultDescription = product?.descriptions?.[0];
  const defaultDescriptionHash = defaultDescription?.hash;

  // Get the description text
  const selectedDescription = getDescription(product, defaultDescriptionHash);
  const description = selectedDescription?.textGenerated || "";

  const productRow = [
    product.manuscript?.filename || "-",
    formatProductProperty(keywordsToText(keywords.generated)),
    {
      richText: themaCodes
        .map((code) => [
          {
            text: `${code.subjectCode} ${code.subjectHeadingText}: `,
            font: { bold: true },
          },
          { text: code.reasoning || "", font: { bold: false } },
          { text: "\n", font: { bold: false } },
        ])
        .flat(),
    },
    formatProductProperty(description),
  ];

  return saveAsExcel(headers, [productRow], filename);
};
